import { saleLineKindSchema } from "@station/pricing-engine";
import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { couponIssuanceSchema, couponStatusSchema } from "./enums.js";
/**
 * Kupon (ADR 0017 §23, §25): işletmenin kuponu kampanya motorunda İNDİRİMDİR — şablon (`coupon_campaigns`) bir
 * `requiresCoupon` motor kampanyasını açar; kodlar (`coupons`) kiracıda tekildir, QR içeriği = kod. Kasa kodu ana veriden
 * doğrular; sunucu çift kullanımı REDDETMEZ, uyarı + alarmla yazar.
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
export const COUPON_CAMPAIGN_STATUSES = ["ACTIVE", "PAUSED", "ENDED"];
export const couponCampaignStatusSchema = z.enum(COUPON_CAMPAIGN_STATUSES);
export const COUPON_CAMPAIGN_STATUS_LABELS = {
    ACTIVE: "Etkin",
    PAUSED: "Duraklatıldı",
    ENDED: "Sona erdi",
};
export const COUPON_REDEMPTION_WARNINGS = [
    "ALREADY_REDEEMED",
    "EXPIRED",
    "NOT_ISSUED",
    "CUSTOMER_MISMATCH",
];
export const couponRedemptionWarningSchema = z.enum(COUPON_REDEMPTION_WARNINGS);
export const COUPON_REDEMPTION_WARNING_LABELS = {
    ALREADY_REDEEMED: "Kupon daha önce kullanılmıştı (çift kullanım)",
    EXPIRED: "Kupon geçerlilik süresi dışında kullanıldı",
    NOT_ISSUED: "İptal edilmiş kupon kullanıldı",
    CUSTOMER_MISMATCH: "Kupon başka bir müşteriye bağlı",
};
/** Kazanım kuponu koşulu: aynı satışta ≥ X miktar (satır türü) ya da sepet neti ≥ Y kuruş. */
export const couponEarnConditionSchema = z.union([
    z
        .object({
        lineKind: saleLineKindSchema,
        minQuantity: quantitySchema.refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır"),
    })
        .strict(),
    z
        .object({
        minBasketNet: moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır"),
    })
        .strict(),
]);
export const couponCampaignSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    campaignId: uuidSchema.describe("Etkiyi taşıyan `requiresCoupon` motor kampanyası"),
    campaignCode: z.string(),
    campaignName: z.string(),
    issuance: couponIssuanceSchema,
    customerBound: z.boolean(),
    validFrom: timestampSchema,
    validUntil: timestampSchema.nullable(),
    codeValidityDays: z.number().int().nullable(),
    maxUsesPerCode: z.number().int(),
    maxUsesPerCustomer: z.number().int().nullable(),
    totalLimit: z.number().int().nullable().describe("Şablonda üretilebilecek en çok kod"),
    earnCondition: couponEarnConditionSchema.nullable(),
    status: couponCampaignStatusSchema,
    stats: z.object({
        total: z.number().int(),
        issued: z.number().int(),
        redeemed: z.number().int(),
        expired: z.number().int(),
        cancelled: z.number().int(),
    }),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const couponCampaignPageSchema = pageOf(couponCampaignSchema);
export const listCouponCampaignsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    status: couponCampaignStatusSchema.optional(),
});
export const createCouponCampaignSchema = z
    .object({
    companyId: uuidSchema,
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9][A-Z0-9_-]{1,39}$/, "Kod büyük harf, rakam, _ ve - (2–40 karakter)"),
    name: z.string().trim().min(1).max(120),
    campaignId: uuidSchema,
    issuance: couponIssuanceSchema,
    customerBound: z.boolean().default(false),
    validFrom: z.iso.datetime({ offset: true }),
    validUntil: z.iso.datetime({ offset: true }).nullable().default(null),
    codeValidityDays: z.number().int().min(1).max(3650).nullable().default(null),
    maxUsesPerCode: z.number().int().min(1).max(1000).default(1),
    maxUsesPerCustomer: z.number().int().positive().max(1_000_000).nullable().default(null),
    totalLimit: z.number().int().positive().max(10_000_000).nullable().default(null),
    earnCondition: couponEarnConditionSchema.nullable().default(null),
})
    .superRefine((value, context) => {
    const issue = (path, message) => context.addIssue({ code: "custom", path: [path], message });
    if ((value.issuance === "EARNED") !== (value.earnCondition !== null))
        issue("earnCondition", "Kazanım koşulu yalnızca (ve mutlaka) kazanım kuponunda girilir");
    if (value.issuance === "EARNED" && !value.customerBound)
        issue("customerBound", "Kazanım kuponu müşteriye bağlıdır");
    if (value.issuance === "EARNED" && value.codeValidityDays === null)
        issue("codeValidityDays", "Kazanım kuponunun geçerlilik süresi (gün) girilmelidir");
    if (value.validUntil && Date.parse(value.validUntil) <= Date.parse(value.validFrom))
        issue("validUntil", "Bitiş başlangıçtan sonra olmalıdır");
});
/** Şirket, motor kampanyası, veriliş biçimi ve koşul DEĞİŞMEZ (üretilmiş kodların anlamı korunur). */
export const updateCouponCampaignSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    validUntil: z.iso.datetime({ offset: true }).nullable().optional(),
    codeValidityDays: z.number().int().min(1).max(3650).nullable().optional(),
    maxUsesPerCustomer: z.number().int().positive().max(1_000_000).nullable().optional(),
    totalLimit: z.number().int().positive().max(10_000_000).nullable().optional(),
    status: couponCampaignStatusSchema.optional(),
    version: versionSchema,
});
export const generateCouponCodesSchema = z.object({
    count: z.number().int().min(1).max(5000),
    prefix: z
        .string()
        .trim()
        .max(12)
        .regex(/^[A-Za-z0-9]*$/, "Önek yalnızca harf ve rakam")
        .optional()
        .describe("Boşsa şablon kodu (harf / rakam kısmı)"),
});
export const generateCouponCodesResultSchema = z.object({
    couponCampaignId: uuidSchema,
    created: z.number().int(),
    codes: z.array(z.string()),
});
export const couponSchema = z.object({
    id: uuidSchema,
    couponCampaignId: uuidSchema,
    campaignCode: z.string(),
    code: z.string(),
    customerProfileId: uuidSchema.nullable(),
    customerCode: z.string().nullable().describe("Kupon sahibi müşterinin kodu (müşteriye bağlı değilse null)"),
    customerMaskedName: z
        .string()
        .nullable()
        .describe("Kupon sahibinin MASKELİ adı (`A*** Y***`; adsız / anonim profilde `—`; müşteriye bağlı değilse null)"),
    status: couponStatusSchema,
    uses: z.number().int(),
    maxUses: z.number().int(),
    validFrom: timestampSchema,
    validUntil: timestampSchema.nullable(),
    issuedAt: timestampSchema,
    sourceSaleId: uuidSchema.nullable(),
    redeemedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    version: z.number().int(),
});
export const couponPageSchema = pageOf(couponSchema);
export const couponRedemptionSchema = z.object({
    id: uuidSchema,
    posSaleId: uuidSchema,
    stationId: uuidSchema,
    customerProfileId: uuidSchema.nullable(),
    redeemedAt: timestampSchema,
    warning: couponRedemptionWarningSchema.nullable(),
    reversedAt: timestampSchema.nullable(),
    reversalSaleId: uuidSchema.nullable(),
});
export const couponDetailSchema = couponSchema.extend({
    companyId: uuidSchema,
    campaignId: uuidSchema.describe("Motor kampanyası"),
    redemptions: z.array(couponRedemptionSchema),
});
export const listCouponsQuerySchema = pageQuerySchema.extend({
    status: couponStatusSchema.optional(),
    customerProfileId: uuidSchema.optional(),
});
/**
 * Müşterinin kuponları (tüm şablonlar; `GET /loyalty/accounts/:customerProfileId/coupons`): çağıranın `loyalty.read`
 * ile gördüğü şirketlerin şablonlarındaki, müşteriye bağlı kodlar — şablon ve motor kampanyası adıyla.
 */
export const customerCouponSchema = couponSchema.extend({
    companyId: uuidSchema,
    couponCampaignName: z.string().describe("Kupon şablonunun adı (`campaignCode` şablonun kodudur)"),
    issuance: couponIssuanceSchema,
    engineCampaignId: uuidSchema.describe("Etkiyi taşıyan `requiresCoupon` motor kampanyası"),
    engineCampaignName: z.string().describe("Motor kampanyasının adı"),
});
export const customerCouponPageSchema = pageOf(customerCouponSchema);
export const listCustomerCouponsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional().describe("Yalnızca bu şirketin şablonları (şirket kapsamı denetlenir)"),
    status: couponStatusSchema.optional(),
});
/** Dışa aktarım (CSV'ye dönüştürülmeye uygun düz satırlar); en çok 20.000 kod. */
export const couponExportSchema = z.object({
    couponCampaignId: uuidSchema,
    campaignCode: z.string(),
    generatedAt: timestampSchema,
    columns: z.array(z.string()),
    items: z.array(z.object({
        code: z.string(),
        status: couponStatusSchema,
        customerProfileId: uuidSchema.nullable(),
        customerCode: z.string().nullable().describe("Sahip müşterinin kodu (müşteriye bağlı değilse null)"),
        customerMaskedName: z
            .string()
            .nullable()
            .describe("Sahibin maskeli adı — dışa aktarımda açık ad YOK (müşteriye bağlı değilse null)"),
        uses: z.number().int(),
        validFrom: timestampSchema,
        validUntil: timestampSchema.nullable(),
        issuedAt: timestampSchema,
    })),
});
export const issueCouponSchema = z.object({
    customerProfileId: uuidSchema,
    validUntil: z.iso.datetime({ offset: true }).nullable().default(null).describe("Boşsa şablonun kuralı"),
});
export const assignCouponSchema = z.object({ customerProfileId: uuidSchema });
export const cancelCouponSchema = z.object({ reason: z.string().trim().min(3).max(300) });
export const couponCodeParamsSchema = z.object({
    code: z.string().trim().min(4).max(60).describe("Kupon kodu (büyük / küçük harf duyarsız)"),
});
//# sourceMappingURL=coupons.js.map